import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import { FundingActivityStatus } from "../enums";
import {
  IAPIFundingDetailModel,
  IAPIFundingDetailPageResponseModel,
  IAPIFundingMerchantDataResponseModel,
  IAPIFundingPayoutsGridModel,
  IAPIPayoutPaymentGridModel,
} from "../interfaces/IAPIFundingPayoutsModel";
import { SystemColor, SystemColorsModel } from "./SystemColorsModel";
import { PurchaseMerchantAccountSettingsModel } from "./MerchantApplicationInputModel";
import { PaymentMethodModel } from "./PaymentMethodModels";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { FundingUtil } from "../utils";

export class FundingDetailPageModel {
  detail: FundingDetailModel;
  payments: PayoutPaymentGridModel[];
  constructor(data: Partial<FundingDetailPageModel> = {}) {
    this.detail = data.detail || new FundingDetailModel();
    this.payments = data.payments || [];
  }

  static deserializeAPIModel(
    data: IAPIFundingDetailPageResponseModel
  ): FundingDetailPageModel {
    const mappedData: Partial<FundingDetailPageModel> = {
      detail: FundingDetailModel.deserializeAPIModel(data.detail),
      payments: data.payments.map((item) =>
        new PayoutPaymentGridModel().deserializeAPIModel(item)
      ),
    };
    return new FundingDetailPageModel(mappedData);
  }
}
export class FundingDetailModel {
  status: keyof typeof FundingActivityStatus;
  createdDate: Date;
  totalAmount: number;
  totalFees: number;
  processingFees: number;
  additionalFees: number;
  batchFees: number;
  rejectFees: number;
  payoutAmount: number;
  payoutNumber: number;
  payoutId: number;
  merchantAccount: PurchaseMerchantAccountSettingsModel;
  constructor(data: Partial<FundingDetailModel> = {}) {
    this.status = data.status || FundingActivityStatus.Deposited;
    this.createdDate = data.createdDate || new Date();
    this.totalAmount = data.totalAmount || 0;
    this.totalFees = data.totalFees || 0;
    this.processingFees = data.processingFees || 0;
    this.additionalFees = data.additionalFees || 0;
    this.batchFees = data.batchFees || 0;
    this.rejectFees = data.rejectFees || 0;
    this.payoutAmount = data.payoutAmount || 0;
    this.payoutNumber = data.payoutNumber || 0;
    this.payoutId = data.payoutId || 0;
    this.merchantAccount =
      data.merchantAccount || new PurchaseMerchantAccountSettingsModel();
  }

  static deserializeAPIModel(data: IAPIFundingDetailModel): FundingDetailModel {
    const mappedData: Partial<FundingDetailModel> = {
      status: data.status as keyof typeof FundingActivityStatus,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      totalAmount: data.totalAmount,
      totalFees: data.totalFees,
      processingFees: data.processingFees,
      additionalFees: data.additionalFees,
      batchFees: data.batchFees,
      rejectFees: data.rejectFees,
      payoutAmount: data.payoutAmount,
      payoutNumber: data.payoutNumber,
      payoutId: data.payoutId,
      merchantAccount:
        data.merchantAccount &&
        PurchaseMerchantAccountSettingsModel.deserializeAPIModel(
          data.merchantAccount
        ),
    };
    return new FundingDetailModel(mappedData);
  }
}

export class PayoutPaymentGridModel {
  status: string;
  createdDate: Date;
  amount: number;
  contact: ContactReferenceModel;
  paymentMethod: PaymentMethodModel;
  constructor(data: Partial<PayoutPaymentGridModel> = {}) {
    this.status = data.status || "";
    this.createdDate = data.createdDate || new Date();
    this.amount = data.amount || 0;
    this.contact = data.contact || new ContactReferenceModel();
    this.paymentMethod = data.paymentMethod || new PaymentMethodModel();
  }
  deserializeAPIModel(
    data: IAPIPayoutPaymentGridModel
  ): PayoutPaymentGridModel {
    const mappedData: Partial<PayoutPaymentGridModel> = {
      status: data.status,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      amount: data.amount,
      contact:
        data.contact && ContactReferenceModel.deserializeAPIModel(data.contact),
      paymentMethod:
        data.paymentMethod &&
        PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
    };
    return new PayoutPaymentGridModel(mappedData);
  }
  getColorsByStatus(): SystemColor {
    let colors = SystemColorsModel.lightGreyColors();
    switch (this.status.toLowerCase()) {
      case "paid":
        colors = SystemColorsModel.ligthGreenColors();
    }
    return colors;
  }
}
export class FundingPayoutsGridModel {
  id: string;
  createdDate: Date;
  status: keyof typeof FundingActivityStatus;
  payoutNo: string;
  totalPayout: number;
  merchantAccount: PurchaseMerchantAccountSettingsModel;
  bankAccount: string;
  constructor(data: Partial<FundingPayoutsGridModel> = {}) {
    this.id = data.id || "";
    this.createdDate = data.createdDate || new Date();
    this.status = data.status || FundingActivityStatus.Deposited;
    this.payoutNo = data.payoutNo || "";
    this.totalPayout = data.totalPayout || 0;
    this.merchantAccount =
      data.merchantAccount || new PurchaseMerchantAccountSettingsModel();
    this.bankAccount = data.bankAccount || "";
  }

  static deserializeAPIModel(
    data: IAPIFundingPayoutsGridModel
  ): FundingPayoutsGridModel {
    const mappedData: Partial<FundingPayoutsGridModel> = {
      id: data.id,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      payoutNo: data.payoutNo,
      totalPayout: data.totalPayout,
      merchantAccount:
        data.merchantAccount &&
        PurchaseMerchantAccountSettingsModel.deserializeAPIModel(
          data.merchantAccount
        ),
      bankAccount: data.bankAccount,
      status: data.status as keyof typeof FundingActivityStatus,
    };

    return new FundingPayoutsGridModel(mappedData);
  }

  getColorsByStatus(): SystemColor {
    return FundingUtil.getColorsByStatus(this.status);
  }
}

export class FundingSummaryDetailModel {
  id: string;
  name: string;
  bankName: string;
  accountEndingNumber: string;
  balance: number;
  constructor(data: Partial<FundingSummaryDetailModel> = {}) {
    this.id = data.id || "";
    this.name = data.name || "";
    this.bankName = data.bankName || "";
    this.accountEndingNumber = data.accountEndingNumber || "";
    this.balance = data.balance || 0;
  }
  static deserializeAPIModel(
    data: IAPIFundingMerchantDataResponseModel
  ): FundingSummaryDetailModel {
    const mappedData: Partial<FundingSummaryDetailModel> = {
      id: data.id,
      name: data.name,
      bankName: data.bankName,
      accountEndingNumber: data.accountEndingNumber,
      balance: data.balance,
    };
    return new FundingSummaryDetailModel(mappedData);
  }
}
